import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { HotToastService } from '@ngxpert/hot-toast';
import { combineLatest, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { ApiService } from '../api/api.service';
import { ApiResponse } from '../models/ApiResponse.model';
import { ApiLogin } from '../models/Login.model';
import { ApiProfile, ApiServer, ApiUser } from '../models/Profile.model';
import { StateService } from '../state/state.service';
import { AuthGuard } from './auth.guard';
import { DemoModeManagerService } from '../demo/demo-mode-manager.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private api: ApiService,
    private state: StateService,
    private router: Router,
    private injector: Injector,
    private toast: HotToastService,
    private demo: DemoModeManagerService,
  ) {}

  public redirectUrl: string | null = null;
  private redirect() {
    return this.redirectUrl
      ? `?redirect=${encodeURIComponent(this.redirectUrl)}`
      : '';
  }

  loadedServers = false;
  login(user: {
    username: string;
    password: string;
    mfa_token?: string;
    passkey?: boolean;
  }) {
    return this.api.post<ApiLogin>('login', user).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status == 0 || err.status == 429) {
          return of({
            success: false,
            message: 'You are being rate limited. Please try again later.',
          } as ApiLogin);
        }

        return of(null);
      }),
      switchMap((res) => {
        if (!res.success && !res.mfa && res?.message) {
          this.router.navigate([`login/error${this.redirect()}`], {
            state: {
              errMessage: res?.message ?? 'Unknown error',
            },
          });
          return of(false);
        }

        if (!res.success) {
          if (res.mfa) {
            this.toast.info(res.message);

            this.router.navigateByUrl(`login/mfa${this.redirect()}`, {
              state: { username: user.username, password: user.password },
            });
          } else {
            this.toast.error('Invalid username or password');
          }

          return of(false);
        }

        const { token } = res;
        this.state.set({ token });

        return this.profile().pipe(
          map((profile) => {
            if (this.redirectUrl) {
              if (this.redirectUrl.startsWith('/')) {
                this.router.navigate([this.redirectUrl]);
              } else {
                window.location.href = this.redirectUrl;
              }
            } else if (res.server_count == 0 && !this.demo.demoModeEnabled()) {
              console.log('login');
              this.router.navigate(['profile']);
            } else {
              this.router.navigate(['dashboard']);
            }
            return true;
          }),
        );
      }),
    );
  }

  loginSocial(token: string, provider: string) {
    const redirectUri = window?.location?.href.split('?')[0];
    return this.api
      .post<ApiLogin>('social-login', {
        token,
        provider,
        redirect_uri: redirectUri,
      })
      .pipe(
        tap((res) => {
          if (!res.success) {
            return;
          }
          this.state.set({ token: res.token });
        }),
        switchMap((res) => {
          if (!res?.success) {
            this.router.navigate([`login/error${this.redirect()}`], {
              state: {
                errMessage: `Couldn't login with Discord: ${res.message}`,
              },
            });
            return of(false);
          }

          return this.profile().pipe(
            map((profile) => {
              if (this.redirectUrl) {
                if (this.redirectUrl.startsWith('/')) {
                  this.router.navigate([this.redirectUrl]);
                } else {
                  window.location.href = this.redirectUrl;
                }
              } else if (
                res.server_count == 0 &&
                !this.demo.demoModeEnabled()
              ) {
                console.log('login2');
                this.router.navigate(['profile']);
              } else {
                this.router.navigate(['dashboard']);
              }
              return true;
            }),
          );
        }),
      );
  }

  logout(redirect = true) {
    if (this.state.get('user')) {
      console.log('logout', this.state.get('user'));
      this.api.post('logout').subscribe({
        next: (e) => {
          if (e.success) {
            if (redirect) this.logoutRedirect();
            this.state.set({
              user: null,
              servers: [],
              selectedServerIdx: 0,
              token: '',
              profileCached: new Date(0),
            });
          }
        },
        error: () => {
          this.toast.error('Failed to logout');
        },
      });
    }

    // if (!this.state.get('user') && redirect) this.logoutRedirect();
  }

  logoutRedirect() {
    this.router.navigate(['login']);
    this.injector.get<AuthGuard>(AuthGuard).loading$.next(false);
  }

  profile(bustCache = false) {
    if (!bustCache) {
      const cacheTime = this.state.get<Date>('profileCached');
      // cache for 2 minutes
      if (new Date().getTime() - cacheTime.getTime() < 120 * 1000) {
        const user = this.state.select<ApiUser>('user');

        return user;
      }
    }

    return combineLatest([
      this.api.post<ApiProfile>('dash/profile'),
      this.api.get<ApiResponse<{ servers: ApiServer[] }>>('user/servers'),
    ]).pipe(
      catchError((err) => {
        console.log('Request error: ', err);
        return of(null);
      }),
      // this is gay
      map(([profile, servers]) => {
        if (!profile.success) {
          profile = null;
        }

        if (!servers.success) {
          servers = null;
        }

        window?.dispatchEvent(
          new CustomEvent('profile:loaded', { detail: { profile, servers } }),
        );
        return { profile, servers };
      }),
      tap(({ profile, servers }) => {
        if (!profile) {
          return;
        }
        this.state.set({
          user: profile.user,
          flags: profile.user.flags,
          featureFlags: profile.user.features.reduce(
            (acc, f) => {
              acc[f.name] = Boolean(f.value);
              return acc;
            },
            {} as { [key: string]: boolean },
          ),
          profileCached: new Date(),
        });

        if (servers) {
          this.state.set({ servers: servers.servers });
        }
      }),
      map((res) => {
        return res?.profile?.user;
      }),
    );
  }

  checkSessionSecurity() {
    return this.api.post('session/security/check').pipe(
      map((res) => {
        if (!res.success) {
          this.toast.error(res?.message ?? 'Failed to check session status');
        }
        if (!res.secured) {
          window.dispatchEvent(
            new CustomEvent('security:check', {
              detail: { provider: res.provider },
            }),
          );
        }
        return res;
      }),
    );
  }

  isAdmin() {
    return this.state.get('user')?.permissions.includes('ADMIN');
  }
}
